import { UIEvent, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { fetchGlobalCatalogItems } from 'entities/CatalogItem';
import {
    GLOBAL_CATALOG_ITEMS_PAGE_SIZE,
    GLOBAL_CATALOG_MAX_PAGES,
} from 'entities/CatalogItem/model/consts/catalogItemsConsts';
import {
    getCatalogItemsAreLoading,
    getCatalogItemsLoadingMore,
    getGlobalCatalogCurrentPage,
    getGlobalCatalogEndCursor,
    getGlobalCatalogHasNextPage,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import {
    getSelectCategoryId,
    getSelectSubCategoryId,
} from 'entities/Category/model/selectors/categorySelectors';
import { getSelectedPropertyId } from 'entities/Property/model/selectors/PropertySelectors';
import { getSearchGlobalCatalogItemValue } from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';

interface UseGlobalCatalogInfiniteScrollParams {
    enabled?: boolean;
    nearBottomOffset?: number;
}

export const useGlobalCatalogInfiniteScroll = ({
    enabled = true,
    nearBottomOffset = 200,
}: UseGlobalCatalogInfiniteScrollParams = {}) => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const location = useLocation();

    const isLoading = useSelector(getCatalogItemsAreLoading);
    const isLoadingMore = useSelector(getCatalogItemsLoadingMore);
    const hasNextPage = useSelector(getGlobalCatalogHasNextPage);
    const currentPage = useSelector(getGlobalCatalogCurrentPage);
    const endCursor = useSelector(getGlobalCatalogEndCursor);
    const globalSearchValue = useSelector(getSearchGlobalCatalogItemValue);
    const selectedCategoryId = useSelector(getSelectCategoryId);
    const selectedSubCategoryId = useSelector(getSelectSubCategoryId);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const propertyId =
        params.propertyId ||
        location.state?.selectedPropertyId ||
        selectedPropertyId;

    const onScroll = useCallback(
        (event: UIEvent<HTMLDivElement>) => {
            if (
                !enabled ||
                !hasNextPage ||
                currentPage >= GLOBAL_CATALOG_MAX_PAGES ||
                isLoading ||
                isLoadingMore
            ) {
                return;
            }

            const { scrollTop, scrollHeight, clientHeight } =
                event.currentTarget;
            const isNearBottom =
                scrollHeight - (scrollTop + clientHeight) <= nearBottomOffset;

            if (!isNearBottom) {
                return;
            }

            const categoryId = selectedSubCategoryId || selectedCategoryId;

            dispatch(
                fetchGlobalCatalogItems({
                    searchString: globalSearchValue || undefined,
                    categoryId,
                    page: currentPage + 1,
                    pageSize: GLOBAL_CATALOG_ITEMS_PAGE_SIZE,
                    after: endCursor || undefined,
                    propertyId,
                }),
            );
        },
        [
            currentPage,
            dispatch,
            endCursor,
            enabled,
            globalSearchValue,
            hasNextPage,
            isLoading,
            isLoadingMore,
            nearBottomOffset,
            propertyId,
            selectedCategoryId,
            selectedSubCategoryId,
        ],
    );

    return { onScroll };
};
